import React, { createContext, useContext, useEffect, useState, useCallback } from "react";

/**
 * PWA / desktop view toggle.
 *
 *  - "mobile" (default): honours `width=device-width` — the app is fully responsive.
 *  - "desktop"          : sets the viewport meta to `width=1280` so mobile browsers
 *                         zoom out and show the full desktop layout. Preference
 *                         is persisted in localStorage.zoodViewMode.
 *
 * The initial value is set BEFORE React mounts by an inline script in index.html
 * so mobile Safari lands on the correct zoom on first paint.
 */
const KEY = "zoodViewMode";
const Ctx = createContext(null);

function apply(mode) {
  const vp = document.getElementById("zl-viewport");
  if (vp) {
    if (mode === "desktop") vp.setAttribute("content", "width=1280, initial-scale=1");
    else vp.setAttribute("content", "width=device-width, initial-scale=1, viewport-fit=cover");
  }
  document.documentElement.classList.toggle("zl-force-desktop", mode === "desktop");
}

export function ViewModeProvider({ children }) {
  const [mode, setMode] = useState(() => {
    try { return localStorage.getItem(KEY) || "mobile"; } catch { return "mobile"; }
  });
  const [isMobileDevice, setIsMobileDevice] = useState(() =>
    typeof window !== "undefined" && window.matchMedia("(max-width: 768px)").matches
  );

  useEffect(() => {
    apply(mode);
    try { localStorage.setItem(KEY, mode); } catch { /* ignore */ }
  }, [mode]);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const on = () => setIsMobileDevice(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);

  const toggle = useCallback(() => setMode((m) => (m === "desktop" ? "mobile" : "desktop")), []);
  const setDesktop = useCallback(() => setMode("desktop"), []);
  const setMobile = useCallback(() => setMode("mobile"), []);

  return (
    <Ctx.Provider value={{ mode, isMobileDevice, toggle, setDesktop, setMobile }}>
      {children}
    </Ctx.Provider>
  );
}

export function useViewMode() {
  const c = useContext(Ctx);
  if (!c) return { mode: "mobile", isMobileDevice: false, toggle: () => {}, setDesktop: () => {}, setMobile: () => {} };
  return c;
}
